import { useMemo, useCallback } from 'react';
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { shareTokenPageNumberState, shareTokenPageSizeState, shareTokensRequestIdState, shareTokenCurrentTemplateIdState } from './atoms.js';
import { shareTokensSelector, shareTokenTemplatesSelector, shareTokenTemplateDomainsSelector } from './selectors.js';

function useShareTokens(elementId) {
    var _a = useRecoilState(shareTokenPageNumberState(elementId)), pageNumber = _a[0], setPageNumber = _a[1];
    var _b = useRecoilState(shareTokenPageSizeState(elementId)), pageSize = _b[0], setPageSize = _b[1];
    var shareTokensLoadable = useRecoilValueLoadable(shareTokensSelector(elementId));
    var setShareTokensRequestId = useSetRecoilState(shareTokensRequestIdState(elementId));
    var memoShareTokens = useMemo(function () {
        var _a, _b;
        if (shareTokensLoadable && shareTokensLoadable.state === "hasValue") {
            return ((_b = (_a = shareTokensLoadable.contents) === null || _a === void 0 ? void 0 : _a._embedded) === null || _b === void 0 ? void 0 : _b.token) || [];
        }
        else if (shareTokensLoadable && shareTokensLoadable.state === "hasError") {
            console.error("[useShareTokens] error while loading share tokens for element id ".concat(elementId), shareTokensLoadable.contents);
        }
        return [];
    }, [elementId, shareTokensLoadable]);
    var memoTotalCount = useMemo(function () {
        var _a;
        if (shareTokensLoadable && shareTokensLoadable.state === "hasValue") {
            return ((_a = shareTokensLoadable.contents) === null || _a === void 0 ? void 0 : _a.totalCount) || 0;
        }
        return 0;
    }, [shareTokensLoadable]);
    var refreshShareTokens = useCallback(function () {
        setPageNumber(1);
        setShareTokensRequestId(function (requestId) { return requestId + 1; });
    }, [setPageNumber, setShareTokensRequestId]);
    return {
        loading: shareTokensLoadable.state === "loading",
        error: (shareTokensLoadable.state === "hasError" && shareTokensLoadable.contents) || null,
        shareTokens: memoShareTokens,
        pageNumber: pageNumber,
        setPageNumber: setPageNumber,
        pageSize: pageSize,
        setPageSize: setPageSize,
        totalCount: memoTotalCount,
        refreshShareTokens: refreshShareTokens
    };
}
function useShareTokenTemplates() {
    var _a = useRecoilState(shareTokenCurrentTemplateIdState), currentTemplateId = _a[0], setCurrentTemplateId = _a[1];
    var shareTokenTemplatesLoadable = useRecoilValueLoadable(shareTokenTemplatesSelector);
    var shareTokenTemplateDomainsLoadable = useRecoilValueLoadable(shareTokenTemplateDomainsSelector);
    var memoShareTokenTemplates = useMemo(function () {
        if (shareTokenTemplatesLoadable && shareTokenTemplatesLoadable.state === "hasValue") {
            return shareTokenTemplatesLoadable.contents || [];
        }
        else if (shareTokenTemplatesLoadable && shareTokenTemplatesLoadable.state === "hasError") {
            console.error("[useShareTokens] error while loading share token templates", shareTokenTemplatesLoadable.contents);
        }
        return [];
    }, [shareTokenTemplatesLoadable]);
    var memoShareTokenTemplateDomains = useMemo(function () {
        if (shareTokenTemplateDomainsLoadable &&
            shareTokenTemplateDomainsLoadable.state === "hasValue") {
            return shareTokenTemplateDomainsLoadable.contents || [];
        }
        else if (shareTokenTemplateDomainsLoadable &&
            shareTokenTemplateDomainsLoadable.state === "hasError") {
            console.error("[useShareTokens] error while loading share token domains", shareTokenTemplateDomainsLoadable.contents);
        }
        return [];
    }, [shareTokenTemplateDomainsLoadable]);
    return {
        loading: shareTokenTemplatesLoadable.state === "loading" ||
            shareTokenTemplateDomainsLoadable.state === "loading",
        error: (shareTokenTemplatesLoadable.state === "hasError" && shareTokenTemplatesLoadable.contents) ||
            (shareTokenTemplateDomainsLoadable.state === "hasError" &&
                shareTokenTemplateDomainsLoadable.contents) ||
            null,
        shareTokenTemplates: memoShareTokenTemplates,
        shareTokenTemplateDomains: memoShareTokenTemplateDomains,
        currentTemplateId: currentTemplateId,
        setCurrentTemplateId: setCurrentTemplateId
    };
}

export { useShareTokens as default, useShareTokenTemplates };
//# sourceMappingURL=hooks.js.map
